import React from 'react'
import { Avatar } from 'antd'
import {
  shareholderTypes,
  sectors,
  capTypes,
  antiDilutionCompensation,
  antiDilutionDuration,
  antiDilutionTransferTypes,
  antiDilutionTypes,
  accumulationTypes,
  roundingModes,
  roundTypes,
  currencies,
  projectRoles,
  dilutionTypes,
  valuationTypes,
} from 'utils/enums'
import { showDate, money, percent, skalar } from 'utils/format'

export const getEntityName = (entityName, name, translate, prefix = '"', suffix = '"') => {
  return translate('entity.name', {
    name: name ? `${prefix}${name}${suffix}` : '',
    entityName: entityName ? translate(`entity.${entityName}`) : '',
  })
}

export const findEntityName = (entityName, entityId, project) => {
  const findEntity = () => {
    switch (entityName) {
      case 'Investor':
        return project.stakeholders.find((s) => s.id === entityId)
      case 'Company':
        return project.company
      case 'InvestmentRound':
        return project.rounds.find((r) => r.id === entityId)
      case 'Share':
        return project.shares.find((s) => s.id === entityId)
      case 'PayOut':
        return project.payOuts.find((p) => p.id === entityId)
      case 'BaseProject':
        return project
      case 'ProjectDocument':
        return [
          project.projectDocuments.find(
            (d) =>
              `ProjectDocumentId(projectId=${d.id.projectId}, blobName=${d.id.blobName})` ===
              entityId,
          ),
          (d) => d && d.id.blobName,
        ]
      case 'ProjectMembership':
        return [
          project.projectMemberships.find(
            (d) =>
              `ProjectMembershipId(projectId=${project.id}, userId=${d.userAccount.userId})` ===
              entityId,
          ),
          (d) => d && `${d.userAccount.firstname} ${d.userAccount.lastname}`,
        ]
      case 'ProjectInvitation':
        return [
          project.projectInvitations.find(
            (d) =>
              `ProjectInvitationId(projectId=${project.id}, userEmail=${d.userEmail})` === entityId,
          ),
          (d) => d && d.userEmail,
        ]
      case 'EquityInvestment':
      case 'CnInvestment':
      case 'CnConversion':
        return [project.events.find((e) => e.id === entityId), (e) => e && e.investor.name]
      case 'BuyOut':
        return [
          project.buyOuts.find((e) => e.id === entityId),
          (b) => b && `${b.seller.name} -> ${b.buyer.name}`,
        ]
      case 'ManualShares':
        return [project.events.find((e) => e.id === entityId), (e) => e && e.investor.name]
      case 'EsopCreation':
        return [project.esops.find((e) => e.id === entityId), (e) => e && e.esop.name]
      case 'EsopGrant':
        return [project.esopGrants.find((e) => e.id === entityId), (e) => e && e.receiver.name]
      default:
        return null
    }
  }

  const entity = findEntity()
  if (!entity) return null
  return Array.isArray(entity) ? entity[1](entity[0]) : entity.name
}

// Ignore changes to this entities (covered by their container-entities)
export const cascadedEntities = [
  'Address',
  'VestingSchedule',
  'LiquidationPreference',
  'AntiDilution',
  'InterestRate',
  'ConvertibleNote',
  'Esop',
  'Contract',
  'BonusCompensationLevel',
]

// Property maps for all entities
// Each property references the formatting function
// Additionally it should have an entry in the translation file of the format: "entity.[entityName].[propertyName]"
export const getPropertiesMap = (entityName) => {
  switch (entityName) {
    case 'Investor':
      return investorMap
    case 'Company':
      return companyMap
    case 'InvestmentRound':
      return investmentRoundMap
    case 'Share':
      return shareMap
    case 'PayOut':
      return payOutMap
    case 'BaseProject':
      return projectMap
    case 'ProjectDocument':
      return projectDocumentMap
    case 'ProjectMembership':
      return projectMembershipMap
    case 'ProjectInvitation':
      return projectInvitationMap
    case 'EquityInvestment':
      return equityInvestmentMap
    case 'CnInvestment':
      return cnInvestmentMap
    case 'CnConversion':
      return cnConversionMap
    case 'BuyOut':
      return buyOutMap
    case 'ManualShares':
      return manualSharesMap
    case 'EsopCreation':
      return esopCreationMap
    case 'EsopGrant':
      return esopGrantMap
    case 'EsopSetup':
      return esopSetupMap
    default:
      throw new Error(`Entity type ${entityName} is not supported for Auditing yet.`)
  }
}

export const revTypeColor = (revType) => {
  switch (revType) {
    case 'ADD':
      return 'green'
    case 'MOD':
      return 'gold'
    case 'DEL':
      return 'red'
    default:
      return 'purple'
  }
}

export const NA_CHAR = '-'

const investorMap = {
  name: (n) => n,
  type: (t) => shareholderTypes[t],
  email: (t) => t,
  jobTitle: (t) => t,
  salary: (t) => money(t),
}

const companyMap = {
  name: (n) => n,
  sector: (n) => sectors[n],
  location: (n) => n,
  companyURL: (n) => n,
  companyLogoURL: (n) => <Avatar src={n} />,
  exist: (n, translate) => (n ? translate('entity.Company.exist') : NA_CHAR),
  invest: (n, translate) => (n ? translate('entity.Company.invest') : NA_CHAR),
  districtCourt: (n) => n,
  registrationNumber: (n) => n,
  information: (n) => n,
  'address.additional': (n) => n,
  'address.street': (n) => n,
  'address.city': (n) => n,
  'address.postcode': (n) => n,
  'address.country': (n) => n,
}

const investmentRoundMap = {
  date: (n) => showDate(n),
  name: (n) => n,
  type: (n) => roundTypes[n],
  cashIn: (n) => n,
  preMoneyValuation: (n) => money(n),
  postMoneyValuation: (n, translate) => (n ? money(n) : translate('general.automatic')),
  'share.name': (n) => n,
}

const shareMap = {
  name: (n) => n,
  seniority: (n) => skalar(n),
  votingRights: (n, translate) => translate(n ? 'yes' : 'no'),
  text: (n) => n,
  'liquidationPreference.multiple': (n) => skalar(n),
  'liquidationPreference.simple': (n, translate) =>
    n
      ? translate('entity.Share.liquidationPreference.simple.SLP')
      : translate('entity.Share.liquidationPreference.simple.PLP'),
  'liquidationPreference.agreedConversion': (n, translate) =>
    n ? translate('entity.Share.liquidationPreference.agreedConversion') : NA_CHAR,
  'liquidationPreference.senior': (n, translate) =>
    n ? translate('entity.Share.liquidationPreference.senior') : NA_CHAR,
  'liquidationPreference.capType': (n) => capTypes[n],
  'liquidationPreference.cap': (n) => money(n),
  'liquidationPreference.customSharePrice': (n) => money(n),
  'liquidationPreference.interestRate.interestRate': (n) => percent(n),
  'liquidationPreference.interestRate.cumulative': (n, translate) => translate(n ? 'yes' : 'no'),
  'liquidationPreference.interestRate.accumulationType': (n) => accumulationTypes[n],
  'liquidationPreference.interestRate.retainDividends': (n, translate) =>
    translate(n ? 'yes' : 'no'),
  'liquidationPreference.interestRate.limitInMonths': (n) => skalar(n),
  'liquidationPreference.interestRate.limitAsDate': (n) => showDate(n),
  'antiDilution.type': (n) => antiDilutionTypes[n],
  'antiDilution.compensation': (n) => antiDilutionCompensation[n],
  'antiDilution.duration': (n) => antiDilutionDuration[n],
  'antiDilution.transferType': (n) => antiDilutionTransferTypes[n],
}

const payOutMap = {
  name: (n) => n,
  seniority: (n) => skalar(n),
  amount: (n) => money(n),
  receivers: {
    name: (n) => n,
  },
  distributionTable: {
    id: 'investor.id',
    'investor.name': (n) => n,
    amount: (n) => money(n),
  },
}

const projectMap = {
  name: (n) => n,
  roundingMode: (n) => roundingModes[n],
  currencyCode: (n) => `${currencies[n].symbol} (${currencies[n].name})`,
  simulateAD: (n, translate) => translate(n ? 'general.enabled' : 'general.disabled'),
}

const projectDocumentMap = {
  title: (n) => n,
  dateAdded: (n) => showDate(n),
  'uploader.firstname': (n) => n,
  'uploader.lastname': (n) => n,
}

const projectMembershipMap = {
  projectRole: (n) => projectRoles[n],
  'defaultInvestor.name': (n) => n,
  'userAccount.firstname': (n) => n,
  'userAccount.lastname': (n) => n,
}

const projectInvitationMap = {
  userEmail: (n) => n,
  guestName: (n) => n,
  projectRole: (n) => projectRoles[n],
  'defaultInvestor.name': (n) => n,
}

const investmentMap = {
  'round.name': (n) => n,
  'investor.name': (n) => n,
  amount: (n) => money(n),
}

const vestingScheduleMap = {
  'vestingSchedule.cliff': (n) => skalar(n),
  'vestingSchedule.cliffPercent': (n) => percent(n),
  'vestingSchedule.accelerationOnExit': (n, translate) => translate(n ? 'yes' : 'no'),
  'vestingSchedule.startDate': (d) => showDate(d),
  'vestingSchedule.percentUnderlyingVesting': (n) => percent(n),
  'vestingSchedule.schedule': {
    percent: (n) => percent(n),
    months: (n) => skalar(n),
    vestingTime: (n) => n,
  },
}

const equityInvestmentMap = {
  ...investmentMap,
  amountOfShares: (n) => n,
  pricePerShare: (n) => money(n),
  date: (d) => showDate(d),
  'share.name': (n) => n,
  ...vestingScheduleMap,
}

const cnInvestmentMap = {
  ...investmentMap,
  'convertibleNote.discount': (n) => percent(n),
  'convertibleNote.dateOfDeposit': (n) => showDate(n),
  'convertibleNote.maturityDate': (n) => showDate(n),
  'convertibleNote.preMoneyFloor': (n) => money(n),
  'convertibleNote.preMoneyCap': (n) => money(n),
  'convertibleNote.interestRate.interestRate': (n) => percent(n),
  'convertibleNote.interestRate.cumulative': (n, translate) => translate(n ? 'yes' : 'no'),
  'convertibleNote.interestRate.accumulationType': (n) => accumulationTypes[n],
  'convertibleNote.interestRate.retainDividends': (n, translate) => translate(n ? 'yes' : 'no'),
  'convertibleNote.interestRate.limitInMonths': (n) => skalar(n),
  'convertibleNote.interestRate.limitAsDate': (n) => showDate(n),
  'convertibleNote.share.name': (n) => n,
  'convertibleNote.forcedPaymentOnConversion': (n, translate) => (n ? money(n) : translate('no')),
}

const cnConversionMap = {
  amountOfShares: (n) => n,
  'round.name': (n) => n,
  'investor.name': (n) => n,
  pricePerShare: (n) => money(n),
  date: (d) => showDate(d),
  'share.name': (n) => n,
  interestAmount: (n) => money(n),
  nominalAmount: (n) => money(n),
}

const buyOutMap = {
  'round.name': (n) => n,
  'buyer.name': (n) => n,
  'seller.name': (n) => n,
  amount: (n) => money(n),
  pricePerShare: (n) => money(n),
  'buyingShare.name': (n) => n,
  'sellingShare.name': (n) => n,
  ...vestingScheduleMap,
}

const manualSharesMap = {
  name: (n) => n,
  'round.name': (n) => n,
  'investor.name': (n) => n,
  'share.name': (n) => n,
  investment: (n) => money(n),
  date: (d) => showDate(d),
  price: (n) => money(n),
  amountOfShares: (n) => skalar(n),
  isVirtualBacking: (n, translate) => translate(n ? 'yes' : 'no'),
  notes: (n) => n,
}

const esopCreationMap = {
  'round.name': (n) => n,
  'esop.name': (n) => n,
  'esop.dilutionType': (n) => dilutionTypes[n],
  'esop.notes': (n) => n,
  valuationType: (n) => valuationTypes[n],
  shareAmount: (n) => skalar(n),
  backingTable: {
    'investor.name': (n) => n,
    numberOfShares: (n) => skalar(n),
  },
  'defaultVestingSchedule.cliff': (n) => skalar(n),
  'defaultVestingSchedule.cliffPercent': (n) => percent(n),
  'defaultVestingSchedule.accelerationOnExit': (n, translate) => translate(n ? 'yes' : 'no'),
  'defaultVestingSchedule.schedule': {
    percent: (n) => percent(n),
    months: (n) => skalar(n),
    vestingTime: (n) => n,
  },
}

const esopGrantMap = {
  'receiver.name': (n) => n,
  'round.name': (n) => n,
  strikePrice: (n) => money(n),
  date: (n) => showDate(n),
  expirationDate: (n) => showDate(n),
  shareAmount: (n) => skalar(n),
  'esop.name': (n) => n,
  'packageCalculation.packageValue': (n) => money(n),
  'packageCalculation.salaryCompensation': (n) => money(n),
  'packageCalculation.bonusCompensation': (n) => money(n),
  'packageCalculation.compensationPeriodInMonths': (n) => skalar(n),
  ...vestingScheduleMap,
}

const esopSetupMap = {
  exitAmount: (n) => money(n),
  exitDate: (n) => showDate(n),
  defaultCompensationPeriodInMonths: (n) => skalar(n),
  bonusCompensationLevels: {
    name: (n) => n,
    percentFromSalary: (n) => percent(n),
  },
}
